
import { Component, Prop, Watch, Ref, Vue } from 'nuxt-property-decorator';
import { pick, isEqual } from 'lodash-es';
import type { InputFilePartialValue } from '../../types';
import type { FormFieldDto } from '../../services';
import { UploadFileType } from '../../services';
import InputFile from '../design-system/input/input-file/InputFile.vue';
import InputText from '../design-system/input/input-text/InputText.vue';
import InputTextArea from '../design-system/input/input-text/InputTextArea.vue';
import FigureModal from './FigureModal.vue';

type FigureEntry = {
  id: string;
  title: string;
  description: string;
};

@Component({
  components: {
    InputText,
    InputTextArea,
    InputFile,
    FigureModal,
  },
})
export default class FormFiguresInputFile extends Vue {
  @Prop({ required: true }) readonly field!: FormFieldDto;
  @Prop({ type: Array, default: Array }) readonly value!: string[];
  @Prop(Boolean) readonly disabled!: boolean;

  @Ref() readonly figureDetailsModal?: FigureModal;

  UploadFileType = UploadFileType;
  fileList: InputFilePartialValue[] = [];
  figureMeta: Record<string, FigureEntry> = {};
  editFigureId: string | null = null;

  get parsedValue(): FigureEntry[] {
    return this.value.map<FigureEntry>(entry => {
      const { id, title, description } = JSON.parse(entry);
      return { id, title, description };
    });
  }

  @Watch('fileList', { deep: true })
  onFileListChange() {
    const figureMeta = pick(
      this.figureMeta,
      this.fileList.map(({ resourceId }) => resourceId),
    );

    this.fileList.forEach(({ resourceId, originalFileName }) => {
      if (!figureMeta[resourceId]) {
        this.$set(this.figureMeta, resourceId, { id: resourceId, title: originalFileName || '', description: '' });
      }
    });

    this.emitInputIfChanged();
  }

  created() {
    this.fileList = this.parsedValue.map(entry => ({ resourceId: entry.id }));
    this.figureMeta = this.parsedValue.reduce((figureMeta, entry) => ({ ...figureMeta, [entry.id]: entry }), {});
  }

  public emitInputIfChanged() {
    const value = this.fileList.map(({ resourceId }) => {
      return JSON.stringify({
        id: resourceId,
        title: this.figureMeta[resourceId]?.title || '',
        description: this.figureMeta[resourceId]?.description || '',
      });
    });

    if (!isEqual(value, this.value)) this.$emit('input', value);
  }

  public getFigureTitle(resourceId: string, originalFileName: string) {
    const figure = this.figureMeta[resourceId];
    return figure?.title && figure.title !== originalFileName ? figure.title : null;
  }

  public getFigureDescription(resourceId: string) {
    return this.figureMeta[resourceId]?.description;
  }

  public onEditFigure(resourceId: string, event: MouseEvent) {
    this.editFigureId = resourceId;
    this.figureDetailsModal?.openModal({ ...this.figureMeta[resourceId] }, event);
  }

  public onModalSave(figureMetaModel: { title: string; description: string }) {
    if (this.editFigureId) {
      this.figureMeta[this.editFigureId] = { ...this.figureMeta[this.editFigureId], ...figureMetaModel };
    }
    this.resetFigureEditor();
    this.emitInputIfChanged();
  }

  public resetFigureEditor() {
    this.editFigureId = null;
  }
}
